import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search, Plus, FileText, Layers, Brain, Edit, Trash2, Eye,
  Clock, Send, X, ChevronDown, ChevronUp, GripVertical,
  CheckCircle, AlertCircle, User, Lock, Tag,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminApi } from '../../lib/api/admin'
import type { QuestionTypeItem, QuestionTypeCategoryItem } from '../../lib/api/admin'
import { usePermissionStore } from '../../stores/permissionStore'
import { friendlyApiError } from '../../lib/utils/apiError'
import EvaluationFormInline from '../interviewer/EvaluationFormInline'

// ── Types ────────────────────────────────────────────────────────────────────

interface QuestionItem {
  text: string
}

interface QuestionTemplate {
  id: number
  title: string
  description?: string
  target_role?: string
  tier?: string
  questions: QuestionItem[]
  competencies: string[]
  is_active: boolean
  is_ai_generated: boolean
  created_at?: string
}

interface EvaluationItem {
  id: number
  status: string
  client_name?: string
  interviewer_name?: string
  scheduled_at?: string
  overall_score?: number
  ai_confidence_score?: number
}

interface NeedsEvalItem {
  source: string
  source_id: number
  client_name?: string
  interviewer_name?: string
  scheduled_at: string
  status: 'needs_evaluation'
}

// Unified row for the "All" tab — covers both existing evals and needs-eval sessions
type UnifiedItem =
  | ({ kind: 'eval' } & EvaluationItem)
  | ({ kind: 'needs' } & NeedsEvalItem)

// Shape returned by /evaluations/admin-all
interface AdminAllApiItem {
  kind: 'eval' | 'needs'
  eval_id?: number
  source?: string
  source_id?: number
  status: string
  client_name?: string
  interviewer_name?: string
  scheduled_at?: string
}

type EvalTab = 'all' | 'needs_evaluation' | 'draft' | 'interviewer_review' | 'approved_delivered'

type ModalMode = 'create' | 'view' | 'edit' | null
type QTModalMode = 'create' | 'edit' | null

const emptyQuestion = (): QuestionItem => ({ text: '' })
const emptyForm = () => ({
  title: '',
  target_role: '',
})
const emptyQTForm = () => ({
  name: '',
  description: '',
  display_order: 0,
})

const EVAL_PER_PAGE = 20

// ── Component ────────────────────────────────────────────────────────────────

export default function AdminContent() {
  // Platform timezone for the embedded evaluation panel's interview date, so it
  // matches /admin/sessions instead of rendering in the admin's browser zone.
  const [platformTz, setPlatformTz] = useState<string | undefined>(undefined)
  useEffect(() => {
    adminApi
      .getPublicSettings()
      .then((s) => {
        if (s?.timezone) setPlatformTz(s.timezone as string)
      })
      .catch(() => {
        // Non-fatal: the panel falls back to browser-local.
      })
  }, [])

  const { can } = usePermissionStore()
  const canCreate = can('content', 'create')
  const canEdit = can('content', 'edit')
  const canDelete = can('content', 'delete')

  const [searchParams] = useSearchParams()
  const validTabs = ['templates', 'question-types', 'ai-content']
  const initialTab = validTabs.includes(searchParams.get('tab') ?? '') ? searchParams.get('tab')! : 'templates'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [templates, setTemplates] = useState<QuestionTemplate[]>([])
  const [evaluations, setEvaluations] = useState<EvaluationItem[]>([])
  const [needsEvalItems, setNeedsEvalItems] = useState<NeedsEvalItem[]>([])
  const [allItems, setAllItems] = useState<UnifiedItem[]>([])
  const [evalTab, setEvalTab] = useState<EvalTab>('all')
  const [evalPage, setEvalPage] = useState(1)
  const [evalTotal, setEvalTotal] = useState(0)
  const [evalLastPage, setEvalLastPage] = useState(1)
  const [evalLoading, setEvalLoading] = useState(false)
  const [reviewEvalId, setReviewEvalId] = useState<number | null>(null)
  const [creatingEvalFor, setCreatingEvalFor] = useState<string | null>(null) // "session-{id}" | "booking-{id}"
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null)
  const [selected, setSelected] = useState<QuestionTemplate | null>(null)

  // Form state
  const [form, setForm] = useState(emptyForm())
  const [formQuestions, setFormQuestions] = useState<QuestionItem[]>([emptyQuestion()])

  // ── Question Types state ───────────────────────────────────────────────────
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeItem[]>([])
  const [qtLoading, setQtLoading] = useState(false)
  const [qtModalMode, setQtModalMode] = useState<QTModalMode>(null)
  const [selectedQT, setSelectedQT] = useState<QuestionTypeItem | null>(null)
  const [qtForm, setQtForm] = useState(emptyQTForm())
  const [qtSaving, setQtSaving] = useState(false)
  const [qtError, setQtError] = useState('')

  // Draft categories entered during "New Question Type" — required (min 1).
  interface DraftCategory { label: string; description: string; display_order: number }
  const [qtDraftCategories, setQtDraftCategories] = useState<DraftCategory[]>([])

  // ── Drag-and-drop state (Question Types) ──────────────────────────────────
  const [dragQTId, setDragQTId] = useState<number | null>(null)
  const [dragOverQTId, setDragOverQTId] = useState<number | null>(null)

  // ── Categories modal state ─────────────────────────────────────────────────
  const [categoriesFor, setCategoriesFor] = useState<QuestionTypeItem | null>(null)
  const [categories, setCategories] = useState<QuestionTypeCategoryItem[]>([])
  const [catLoading, setCatLoading] = useState(false)
  const [editingCatId, setEditingCatId] = useState<number | 'new' | null>(null)
  const [catForm, setCatForm] = useState({ key: '', label: '', description: '', display_order: 0 })
  const [catSaving, setCatSaving] = useState(false)
  const [catError, setCatError] = useState('')

  // ── Drag-and-drop state (Categories) ──────────────────────────────────────
  const [dragCatId, setDragCatId] = useState<number | null>(null)
  const [dragOverCatId, setDragOverCatId] = useState<number | null>(null)

  // ── Data fetch ─────────────────────────────────────────────────────────────

  useEffect(() => { fetchData() }, [])
  useEffect(() => {
    if (activeTab === 'question-types') fetchQuestionTypes()
  }, [activeTab])

  const fetchData = async () => {
    try {
      const questionsRes = await adminApi.getQuestions({ limit: 200 })
      const raw = questionsRes?.data ?? questionsRes ?? []
      setTemplates(
        raw.map((t: QuestionTemplate & { questions?: QuestionItem[] | number }) => ({
          ...t,
          questions: Array.isArray(t.questions) ? t.questions : [],
        })),
      )
    } catch {
      setError('Failed to load content')
    } finally {
      setLoading(false)
    }
  }

  const fetchQuestionTypes = async () => {
    setQtLoading(true)
    try {
      const data = await adminApi.getQuestionTypes(true)
      setQuestionTypes(Array.isArray(data) ? data : [])
    } catch {
      // silently ignore
    } finally {
      setQtLoading(false)
    }
  }

  // ── Question Types CRUD helpers ────────────────────────────────────────────

  function openCreateQT() {
    setQtForm(emptyQTForm())
    setQtError('')
    setQtDraftCategories([])
    setQtModalMode('create')
  }

  function openEditQT(qt: QuestionTypeItem) {
    setSelectedQT(qt)
    setQtForm({ name: qt.name, description: qt.description ?? '', display_order: qt.display_order })
    setQtError('')
    setQtModalMode('edit')
  }

  function closeQTModal() {
    setQtModalMode(null)
    setSelectedQT(null)
    setQtError('')
    setQtDraftCategories([])
  }

  async function handleSaveQT(e: React.FormEvent) {
    e.preventDefault()
    if (!qtForm.name.trim()) { setQtError('Name is required.'); return }
    // For create: require at least one category.
    if (qtModalMode === 'create' && qtDraftCategories.length === 0) {
      const msg = 'Add at least one evaluation category before creating this question type.'
      toast.error(msg)
      setQtError(msg)
      return
    }
    setQtSaving(true)
    setQtError('')
    try {
      const payload = {
        name: qtForm.name.trim(),
        description: qtForm.description.trim() || undefined,
        display_order: qtForm.display_order,
      }
      if (qtModalMode === 'create') {
        const created = await adminApi.createQuestionType(payload)
        // Persist draft categories. If any category fails we surface the error
        // but keep the just-created type — admin can retry categories in the modal.
        try {
          for (const dc of qtDraftCategories) {
            const key = dc.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
            await adminApi.createQuestionTypeCategory(created.id, {
              key,
              label: dc.label.trim(),
              description: dc.description.trim(),
              display_order: dc.display_order,
            })
          }
        } catch (catErr) {
          const msg = friendlyApiError(catErr, 'Question type was created but one of the categories failed to save. Open the Categories panel to finish setup.')
          toast.error(msg)
          setQtError(msg)
          // still add the type to the list so admin can open Categories from the row
          setQuestionTypes([...questionTypes, created].sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)))
          return  // keep modal open so admin sees the error
        }
        setQuestionTypes([...questionTypes, created].sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)))
      } else if (qtModalMode === 'edit' && selectedQT) {
        const updated = await adminApi.updateQuestionType(selectedQT.id, payload)
        setQuestionTypes(questionTypes.map((t) => t.id === selectedQT.id ? updated : t).sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)))
      }
      closeQTModal()
    } catch (err) {
      const msg = friendlyApiError(err, 'Failed to save question type')
      toast.error(msg)
      setQtError(msg)
    } finally {
      setQtSaving(false)
    }
  }

  async function handleToggleQT(qt: QuestionTypeItem) {
    try {
      const updated = await adminApi.updateQuestionType(qt.id, { is_active: !qt.is_active })
      setQuestionTypes(questionTypes.map((t) => t.id === qt.id ? updated : t))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to update question type'))
    }
  }

  async function handleDeleteQT(qt: QuestionTypeItem) {
    if (!window.confirm(`Delete question type "${qt.name}"? This cannot be undone.`)) return
    try {
      await adminApi.deleteQuestionType(qt.id)
      setQuestionTypes(questionTypes.filter((t) => t.id !== qt.id))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to delete question type'))
    }
  }

  async function handleDropQT(targetId: number) {
    if (dragQTId === null || dragQTId === targetId) return
    const sorted = [...questionTypes]
    const fromIdx = sorted.findIndex((t) => t.id === dragQTId)
    const toIdx = sorted.findIndex((t) => t.id === targetId)
    if (fromIdx === -1 || toIdx === -1) return
    // Move dragged item to target position
    const reordered = [...sorted]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    setQuestionTypes(reordered)
    setDragQTId(null)
    setDragOverQTId(null)
    try {
      await adminApi.reorderQuestionTypes(reordered.map((t) => t.id))
      const fresh = await adminApi.getQuestionTypes(true)
      setQuestionTypes(Array.isArray(fresh) ? fresh : reordered)
    } catch (err) {
      setQuestionTypes(sorted)
      alert(friendlyApiError(err, 'Failed to reorder question types'))
    }
  }

  // ── Category CRUD helpers ──────────────────────────────────────────────────

  async function openCategories(qt: QuestionTypeItem) {
    setCategoriesFor(qt)
    setCategories([])
    setEditingCatId(null)
    setCatError('')
    setCatLoading(true)
    try {
      const data = await adminApi.getQuestionTypeCategories(qt.id, true)
      setCategories(data)
    } catch (err) {
      setCatError(friendlyApiError(err, 'Failed to load categories'))
    } finally {
      setCatLoading(false)
    }
  }

  function closeCategories() {
    setCategoriesFor(null)
    setCategories([])
    setEditingCatId(null)
    setCatError('')
  }

  function openEditCategory(cat: QuestionTypeCategoryItem) {
    setEditingCatId(cat.id)
    setCatForm({
      key: cat.key,
      label: cat.label,
      description: cat.description,
      display_order: cat.display_order,
    })
    setCatError('')
  }

  function openCreateCategory() {
    // Pick the smallest positive integer not already used by this type's categories.
    const used = new Set(categories.map((c) => c.display_order))
    let nextOrder = 1
    while (used.has(nextOrder)) nextOrder++
    setEditingCatId('new')
    setCatForm({ key: '', label: '', description: '', display_order: nextOrder })
    setCatError('')
  }

  async function handleSaveCategory(e: React.FormEvent) {
    e.preventDefault()
    if (!categoriesFor) return
    if (!catForm.label.trim()) { setCatError('Label is required.'); return }
    if (!catForm.description.trim()) { setCatError('Description is required.'); return }
    // Auto-derive a stable key from the label
    const key = catForm.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    if (!key) { setCatError('Label must contain at least one letter or digit.'); return }
    setCatSaving(true)
    setCatError('')
    try {
      if (editingCatId === 'new') {
        const created = await adminApi.createQuestionTypeCategory(categoriesFor.id, {
          key,
          label: catForm.label.trim(),
          description: catForm.description.trim(),
          display_order: catForm.display_order,
        })
        setCategories([...categories, created].sort((a, b) => a.display_order - b.display_order))
      } else if (typeof editingCatId === 'number') {
        const updated = await adminApi.updateQuestionTypeCategory(editingCatId, {
          key,
          label: catForm.label.trim(),
          description: catForm.description.trim(),
          display_order: catForm.display_order,
        })
        setCategories(
          categories
            .map((c) => (c.id === editingCatId ? updated : c))
            .sort((a, b) => a.display_order - b.display_order),
        )
      }
      setEditingCatId(null)
    } catch (err) {
      const msg = friendlyApiError(err, 'Failed to save category')
      toast.error(msg)
      setCatError(msg)
    } finally {
      setCatSaving(false)
    }
  }

  async function handleToggleCategory(cat: QuestionTypeCategoryItem) {
    try {
      const updated = await adminApi.updateQuestionTypeCategory(cat.id, { is_active: !cat.is_active })
      setCategories(categories.map((c) => (c.id === cat.id ? updated : c)))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to update category'))
    }
  }

  async function handleDeleteCategory(cat: QuestionTypeCategoryItem) {
    if (!window.confirm(`Delete category "${cat.label}"?`)) return
    try {
      await adminApi.deleteQuestionTypeCategory(cat.id)
      setCategories(categories.filter((c) => c.id !== cat.id))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to delete category'))
    }
  }

  async function handleDropCat(targetId: number) {
    if (!categoriesFor || dragCatId === null || dragCatId === targetId) return
    const fromIdx = categories.findIndex((c) => c.id === dragCatId)
    const toIdx = categories.findIndex((c) => c.id === targetId)
    if (fromIdx === -1 || toIdx === -1) return
    const reordered = [...categories]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    setCategories(reordered)
    setDragCatId(null)
    setDragOverCatId(null)
    try {
      await adminApi.reorderQuestionTypeCategories(categoriesFor.id, reordered.map((c) => c.id))
      const fresh = await adminApi.getQuestionTypeCategories(categoriesFor.id, true)
      setCategories(Array.isArray(fresh) ? fresh : reordered)
    } catch (err) {
      setCategories(categories)
      alert(friendlyApiError(err, 'Failed to reorder categories'))
    }
  }

  const fetchEvaluations = async (tab: EvalTab, page: number) => {
    setEvalLoading(true)
    try {
      if (tab === 'needs_evaluation') {
        const res = await adminApi.getNeedsEvaluation({ page, per_page: EVAL_PER_PAGE }).catch(() => null)
        setNeedsEvalItems(Array.isArray(res?.data) ? res.data : [])
        setEvalTotal(res?.total ?? 0)
        setEvalLastPage(res?.last_page ?? 1)
      } else if (tab === 'all') {
        const res = await adminApi.getAdminAll({ page, per_page: EVAL_PER_PAGE }).catch(() => null)
        const unified: UnifiedItem[] = (Array.isArray(res?.data) ? res.data : []).map((item: AdminAllApiItem) =>
          item.kind === 'eval'
            ? {
                kind: 'eval' as const,
                id: item.eval_id ?? 0,
                status: item.status,
                client_name: item.client_name,
                interviewer_name: item.interviewer_name,
                scheduled_at: item.scheduled_at,
              }
            : {
                kind: 'needs' as const,
                source: item.source ?? '',
                source_id: item.source_id ?? 0,
                status: 'needs_evaluation' as const,
                client_name: item.client_name,
                interviewer_name: item.interviewer_name,
                scheduled_at: item.scheduled_at ?? '',
              }
        )
        setAllItems(unified)
        setEvalTotal(res?.total ?? 0)
        setEvalLastPage(res?.last_page ?? 1)
      } else {
        const params: { status?: string; page: number; per_page: number } = { page, per_page: EVAL_PER_PAGE }
        if (tab === 'draft') params.status = 'draft'
        else if (tab === 'interviewer_review') params.status = 'interviewer_review'
        else if (tab === 'approved_delivered') params.status = 'approved_delivered'
        const res = await adminApi.getEvaluations(params).catch(() => null)
        setEvaluations(Array.isArray(res?.data) ? res.data : [])
        setEvalTotal(res?.total ?? 0)
        setEvalLastPage(res?.last_page ?? 1)
      }
    } finally {
      setEvalLoading(false)
    }
  }

  // Reset page to 1 when tab changes
  useEffect(() => { setEvalPage(1) }, [evalTab])

  // Fetch when active section, tab, or page changes
  useEffect(() => {
    if (activeTab === 'ai-content') {
      fetchEvaluations(evalTab, evalPage)
    }
  }, [activeTab, evalTab, evalPage])

  // ── Derived ────────────────────────────────────────────────────────────────

  // Items for the current page (already server-paginated — no client slice needed)
  const evalPageItems: UnifiedItem[] | NeedsEvalItem[] | EvaluationItem[] =
    evalTab === 'all' ? allItems :
    evalTab === 'needs_evaluation' ? needsEvalItems :
    evaluations

  const roles = [...new Set(templates.map((t) => t.target_role).filter(Boolean))] as string[]

  const filtered = templates.filter((t) => {
    const matchSearch = t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.target_role ?? '').toLowerCase().includes(search.toLowerCase())
    const matchRole = !filterRole || t.target_role === filterRole
    return matchSearch && matchRole
  })

  // ── Modal helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setForm(emptyForm())
    setFormQuestions([emptyQuestion()])
    setError('')
    setModalMode('create')
  }

  function openView(t: QuestionTemplate) {
    setSelected(t)
    setModalMode('view')
  }

  function openEdit(t: QuestionTemplate) {
    setSelected(t)
    setForm({
      title: t.title,
      target_role: t.target_role ?? '',
    })
    setFormQuestions(t.questions.length > 0 ? t.questions.map((q) => ({ ...q })) : [emptyQuestion()])
    setError('')
    setModalMode('edit')
  }

  function closeModal() {
    setModalMode(null)
    setSelected(null)
    setError('')
  }

  // ── Question list helpers ─────────────────────────────────────────────────

  function addQuestion() {
    setFormQuestions([...formQuestions, emptyQuestion()])
  }

  function removeQuestion(idx: number) {
    setFormQuestions(formQuestions.filter((_, i) => i !== idx))
  }

  function updateQuestion(idx: number, field: keyof QuestionItem, value: string) {
    setFormQuestions(formQuestions.map((q, i) => i === idx ? { ...q, [field]: value } : q))
  }

  // ── CRUD ──────────────────────────────────────────────────────────────────

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const validQs = formQuestions.filter((q) => q.text.trim())
    if (validQs.length === 0) { setError('Add at least one question.'); return }
    setSaving(true)
    setError('')
    try {
      const payload = {
        title: form.title.trim(),
        target_role: form.target_role.trim() || null,
        questions: validQs,
        competencies: [],
        is_ai_generated: false,
      }
      if (modalMode === 'create') {
        const created = await adminApi.createQuestion(payload)
        const norm: QuestionTemplate = { ...created, questions: Array.isArray(created.questions) ? created.questions : [] }
        setTemplates([norm, ...templates])
      } else if (modalMode === 'edit' && selected) {
        const updated = await adminApi.updateQuestion(selected.id, payload)
        const norm: QuestionTemplate = { ...updated, questions: Array.isArray(updated.questions) ? updated.questions : [] }
        setTemplates(templates.map((t) => t.id === selected.id ? norm : t))
      }
      closeModal()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  async function handleInitReview(item: NeedsEvalItem) {
    const key = `${item.source}-${item.source_id}`
    setCreatingEvalFor(key)
    try {
      const payload = item.source === 'booking'
        ? { booking_id: item.source_id }
        : { session_id: item.source_id }
      const result = await adminApi.adminInitEvaluation(payload)
      const evalId = result?.id ?? result?.data?.id
      if (evalId) {
        setReviewEvalId(evalId)
      } else {
        alert('Failed to create evaluation: no ID returned')
      }
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to create evaluation'))
    } finally {
      setCreatingEvalFor(null)
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Delete this template? This cannot be undone.')) return
    try {
      await adminApi.deleteQuestion(id)
      setTemplates(templates.filter((t) => t.id !== id))
    } catch (err) {
      alert(friendlyApiError(err, 'Failed to delete template'))
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading-screen">Loading content...</div>

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Content & AI Oversight</h1>
        <p className="page-subtitle">Manage question templates and AI-generated content</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>
          <Layers size={16} /> Question Templates
        </button>
        <button className={`tab ${activeTab === 'question-types' ? 'active' : ''}`} onClick={() => setActiveTab('question-types')}>
          <Tag size={16} /> Question Types
        </button>
        <button className={`tab ${activeTab === 'ai-content' ? 'active' : ''}`} onClick={() => setActiveTab('ai-content')}>
          <Brain size={16} /> AI Summaries
        </button>
      </div>

      {/* ── Question Templates Tab ─────────────────────────────────────────── */}
      {activeTab === 'templates' && (
        <div className="card">
          {/* Toolbar */}
          <div className="ac-toolbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px' }}>
              <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                type="text"
                className="form-input"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ border: 'none', boxShadow: 'none', padding: '8px 0' }}
              />
            </div>

            <div className="ac-toolbar-row2">
              {roles.length > 0 && (
                <select
                  className="form-input"
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <option value="">All Roles</option>
                  {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              )}

              {canCreate && (
                <button className="btn btn-primary ac-new-btn" onClick={openCreate}>
                  <Plus size={16} /> New Template
                </button>
              )}
            </div>
          </div>

          {/* Stats bar */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, fontSize: 13, color: 'var(--text-muted)' }}>
            <span><strong style={{ color: 'var(--text-dark)' }}>{templates.length}</strong> total</span>
            <span><strong style={{ color: 'var(--text-dark)' }}>{filtered.length}</strong> shown</span>
            {filterRole && <span>Filtered by: <strong style={{ color: 'var(--primary-navy)' }}>{filterRole}</strong></span>}
          </div>

          {/* Template grid */}
          {filtered.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} />
              <h3>No templates found</h3>
              <p>{search || filterRole ? 'Try adjusting your filters.' : 'Create your first question template.'}</p>
              {!search && !filterRole && canCreate && (
                <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> New Template</button>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 16 }}>
              {filtered.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onView={() => openView(t)}
                  onEdit={() => openEdit(t)}
                  onDelete={() => handleDelete(t.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Question Types Tab ────────────────────────────────────────────── */}
      {activeTab === 'question-types' && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--text-dark)' }}>Question Types</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                Manage the question type options available to interviewers when building question sets.
              </p>
            </div>
            {canCreate && (
              <button className="btn btn-primary" onClick={openCreateQT} style={{ flexShrink: 0 }}>
                <Plus size={16} /> New Question Type
              </button>
            )}
          </div>

          {qtLoading ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}>
              Loading question types...
            </div>
          ) : questionTypes.length === 0 ? (
            <div className="empty-state">
              <Tag size={48} />
              <h3>No question types yet</h3>
              <p>Add question types that interviewers can assign to questions.</p>
              {canCreate && (
                <button className="btn btn-primary" onClick={openCreateQT}><Plus size={16} /> New Question Type</button>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {canEdit && <th style={{ width: 32 }} />}
                    <th style={{ width: 40 }}>#</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: 90 }}>Status</th>
                    <th style={{ width: 230 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questionTypes.map((qt, idx) => {
                    const isDragging = dragQTId === qt.id
                    const isDragOver = dragOverQTId === qt.id
                    return (
                    <tr
                      key={qt.id}
                      draggable={canEdit}
                      onDragStart={() => { setDragQTId(qt.id); setDragOverQTId(null) }}
                      onDragOver={(e) => { e.preventDefault(); if (dragQTId !== qt.id) setDragOverQTId(qt.id) }}
                      onDragLeave={() => setDragOverQTId(null)}
                      onDrop={() => handleDropQT(qt.id)}
                      onDragEnd={() => { setDragQTId(null); setDragOverQTId(null) }}
                      style={{
                        opacity: isDragging ? 0.4 : qt.is_active ? 1 : 0.55,
                        background: isDragOver ? 'rgba(99,102,241,0.07)' : undefined,
                        outline: isDragOver ? '2px dashed #6366f1' : undefined,
                        outlineOffset: -2,
                        transition: 'background 0.15s, outline 0.15s',
                        cursor: canEdit ? 'grab' : undefined,
                      }}
                    >
                      {canEdit && (
                        <td style={{ color: 'var(--text-muted)', paddingRight: 4, cursor: 'grab' }} title="Drag to reorder">
                          <GripVertical size={15} />
                        </td>
                      )}
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)' }}>{qt.name}</span>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 300 }}>
                        {qt.description || <span style={{ fontStyle: 'italic' }}>—</span>}
                      </td>
                      <td>
                        <span className={`badge ${qt.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {qt.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
                          <button
                            className="btn btn-outline"
                            style={{ padding: '5px 10px', fontSize: 12, whiteSpace: 'nowrap' }}
                            onClick={() => openCategories(qt)}
                            title="Manage Categories"
                          >
                            <Layers size={13} /> Categories
                          </button>
                          {canEdit && (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px', fontSize: 12 }}
                              onClick={() => openEditQT(qt)}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className="btn btn-outline"
                              style={{ padding: '5px 10px', fontSize: 12 }}
                              onClick={() => handleToggleQT(qt)}
                              title={qt.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {qt.is_active ? <CheckCircle size={13} style={{ color: '#16a34a' }} /> : <AlertCircle size={13} style={{ color: '#d97706' }} />}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-outline"
                              style={{ padding: '5px 10px', fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }}
                              onClick={() => handleDeleteQT(qt)}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── AI Summaries / Evaluations Tab ─────────────────────────────────── */}
      {activeTab === 'ai-content' && (
        <div className="card">
          {/* AI Summaries header: title + refresh always on one row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>AI-Generated Summaries</h3>
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13, flexShrink: 0 }}
              onClick={() => fetchEvaluations(evalTab, evalPage)} disabled={evalLoading}>
              {evalLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {/* Filter tabs: scrollable row */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as 'auto', marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 2, minWidth: 'max-content' }}>
              {([
                { key: 'all', label: 'All' },
                { key: 'needs_evaluation', label: 'Need Evaluation' },
                { key: 'draft', label: 'Draft' },
                { key: 'interviewer_review', label: 'Under Review' },
                { key: 'approved_delivered', label: 'Approved & Sent' },
              ] as { key: EvalTab; label: string }[]).map(({ key, label }) => (
                <button key={key} type="button"
                  onClick={() => setEvalTab(key)}
                  style={{
                    padding: '8px 14px', fontSize: 13, fontWeight: evalTab === key ? 600 : 400,
                    border: 'none', background: 'none', cursor: 'pointer',
                    borderBottom: evalTab === key ? '2px solid var(--primary-navy)' : '2px solid transparent',
                    color: evalTab === key ? 'var(--primary-navy)' : 'var(--text-muted)',
                    marginBottom: -1, transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {evalLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading evaluations...</div>
          ) : evalTab === 'needs_evaluation' ? (
            /* ── Needs Evaluation table ── */
            <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Interviewer</th>
                    <th>Session Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {evalTotal === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      <CheckCircle size={32} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.3 }} />
                      All sessions have evaluations
                    </td></tr>
                  ) : (evalPageItems as NeedsEvalItem[]).map((item, idx) => (
                    <tr key={`${item.source}-${item.source_id}-${idx}`}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
                            {(item.client_name || 'C').charAt(0).toUpperCase()}
                          </div>
                          <span>{item.client_name || '—'}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <User size={13} style={{ color: 'var(--text-muted)' }} />
                          {item.interviewer_name || '—'}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {item.scheduled_at ? new Date(item.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <AlertCircle size={10} /> Needs Evaluation
                        </span>
                      </td>
                      <td>
                        {canCreate ? (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            disabled={creatingEvalFor === `${item.source}-${item.source_id}`}
                            onClick={() => handleInitReview(item)}
                          >
                            <Eye size={13} />
                            {creatingEvalFor === `${item.source}-${item.source_id}` ? 'Creating...' : 'Review'}
                          </button>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                            <Lock size={12} /> No Access
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {evalTotal > 0 && (
              <EvalPagination page={evalPage} totalPages={evalLastPage} total={evalTotal} onPageChange={setEvalPage} />
            )}
            </>
          ) : evalTab === 'all' ? (
            /* ── All tab: unified evaluations + needs-evaluation sessions ── */
            <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Interviewer</th>
                    <th>Session Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {evalTotal === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      No sessions found
                    </td></tr>
                  ) : (evalPageItems as UnifiedItem[]).map((item, idx) => {
                    const clientName = item.client_name || '—'
                    const date = item.scheduled_at
                      ? new Date(item.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'
                    if (item.kind === 'needs') {
                      return (
                        <tr key={`needs-${item.source}-${item.source_id}-${idx}`}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
                                {clientName.charAt(0).toUpperCase()}
                              </div>
                              <span>{clientName}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                              <User size={13} style={{ color: 'var(--text-muted)' }} />
                              {item.interviewer_name || '—'}
                            </div>
                          </td>
                          <td style={{ fontSize: 13 }}>{date}</td>
                          <td>
                            <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <AlertCircle size={10} /> Needs Evaluation
                            </span>
                          </td>
                          <td>
                            {canCreate ? (
                              <button
                                className="btn btn-primary"
                                style={{ padding: '5px 12px', fontSize: 12 }}
                                disabled={creatingEvalFor === `${item.source}-${item.source_id}`}
                                onClick={() => handleInitReview(item)}
                              >
                                <Eye size={13} />
                                {creatingEvalFor === `${item.source}-${item.source_id}` ? 'Creating...' : 'Review'}
                              </button>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                                <Lock size={12} /> No Access
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    }
                    // 'eval' row — id was set from eval_id during mapping
                    const evalId = item.id
                    return (
                      <tr key={`eval-${evalId}-${idx}`}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
                              {clientName.charAt(0).toUpperCase()}
                            </div>
                            <span>{clientName}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <User size={13} style={{ color: 'var(--text-muted)' }} />
                            {item.interviewer_name || '—'}
                          </div>
                        </td>
                        <td style={{ fontSize: 13 }}>{date}</td>
                        <td><EvalStatusBadge status={item.status} /></td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '5px 12px', fontSize: 12 }}
                            onClick={() => setReviewEvalId(evalId)}
                          >
                            <Eye size={13} /> Review
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {evalTotal > 0 && (
              <EvalPagination page={evalPage} totalPages={evalLastPage} total={evalTotal} onPageChange={setEvalPage} />
            )}
            </>
          ) : (
            /* ── Filtered tabs: draft / under review / approved+sent ── */
            <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Interviewer</th>
                    <th>Session Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {evalTotal === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      No evaluations found
                    </td></tr>
                  ) : (evalPageItems as EvaluationItem[]).map((ev) => (
                    <tr key={ev.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
                            {(ev.client_name || 'C').charAt(0).toUpperCase()}
                          </div>
                          <span>{ev.client_name || '—'}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                          <User size={13} style={{ color: 'var(--text-muted)' }} />
                          {ev.interviewer_name || '—'}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {ev.scheduled_at ? new Date(ev.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td><EvalStatusBadge status={ev.status} /></td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => setReviewEvalId(ev.id)}
                        >
                          <Eye size={13} /> Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {evalTotal > 0 && (
              <EvalPagination page={evalPage} totalPages={evalLastPage} total={evalTotal} onPageChange={setEvalPage} />
            )}
            </>
          )}
        </div>
      )}

      <style>{`
        /* Templates toolbar: search full-width on its own row, then role+button on same row below */
        .ac-toolbar {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 20px;
        }
        .ac-toolbar-row2 {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .ac-toolbar-row2 select { flex: 1; min-width: 0; width: auto; }
        .ac-new-btn { flex-shrink: 0; white-space: nowrap; }

        /* On wider screens: single row layout */
        @media (min-width: 641px) {
          .ac-toolbar { flex-direction: row; align-items: center; }
          .ac-toolbar > div:first-child { flex: 1; min-width: 200px; }
          .ac-toolbar-row2 { flex: none; gap: 10px; }
          .ac-toolbar-row2 select { width: auto; flex: none; min-width: 160px; }
        }
      `}</style>

      {/* ── Categories Modal ───────────────────────────────────────────────── */}
      {categoriesFor && (
        <ModalOverlay onClose={closeCategories}>
          <div className="modal" style={{ maxWidth: 760, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Evaluation Categories</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Manage the rating categories shown to interviewers for <strong>{categoriesFor.name}</strong> questions.
                </p>
              </div>
              <button onClick={closeCategories} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {catLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>Loading categories…</div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {categories.length} {categories.length === 1 ? 'category' : 'categories'} defined
                    </span>
                    {canEdit && editingCatId === null && (
                      <button className="btn btn-primary" onClick={openCreateCategory} style={{ padding: '6px 12px', fontSize: 13 }}>
                        <Plus size={14} /> Add Category
                      </button>
                    )}
                  </div>

                  {categories.length === 0 && editingCatId !== 'new' && (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                      No categories yet. Add one to start scoring this question type.
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {categories.map((cat, catIdx) => {
                      const isCatDragging = dragCatId === cat.id
                      const isCatDragOver = dragOverCatId === cat.id
                      return (
                      <div
                        key={cat.id}
                        draggable={canEdit && editingCatId !== cat.id}
                        onDragStart={() => { setDragCatId(cat.id); setDragOverCatId(null) }}
                        onDragOver={(e) => { e.preventDefault(); if (dragCatId !== cat.id) setDragOverCatId(cat.id) }}
                        onDragLeave={() => setDragOverCatId(null)}
                        onDrop={() => handleDropCat(cat.id)}
                        onDragEnd={() => { setDragCatId(null); setDragOverCatId(null) }}
                        style={{
                          border: isCatDragOver ? '2px dashed #6366f1' : '1px solid var(--border)',
                          borderRadius: 8,
                          padding: 12,
                          opacity: isCatDragging ? 0.4 : cat.is_active ? 1 : 0.6,
                          background: isCatDragOver ? 'rgba(99,102,241,0.05)' : undefined,
                          transition: 'background 0.15s, border 0.15s',
                          cursor: canEdit && editingCatId !== cat.id ? 'grab' : undefined,
                        }}
                      >
                        {editingCatId === cat.id ? (
                          <CategoryEditForm
                            form={catForm}
                            setForm={setCatForm}
                            onSave={handleSaveCategory}
                            onCancel={() => setEditingCatId(null)}
                            saving={catSaving}
                            error={catError}
                            usedOrders={categories.filter((c) => c.id !== cat.id).map((c) => c.display_order)}
                          />
                        ) : (
                          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            {canEdit && (
                              <span title="Drag to reorder" style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', paddingTop: 2 }}>
                                <GripVertical size={15} style={{ color: 'var(--text-muted)', cursor: 'grab' }} />
                              </span>
                            )}
                            <div style={{ minWidth: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', paddingTop: 2, flexShrink: 0 }}>
                              {catIdx + 1}.
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-dark)' }}>
                                {cat.label}
                                {!cat.is_active && (
                                  <span className="badge badge-danger" style={{ marginLeft: 8, fontSize: 10 }}>Inactive</span>
                                )}
                              </div>
                              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
                                {cat.description}
                              </div>
                            </div>
                            {canEdit && (
                              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 11 }}
                                  onClick={() => openEditCategory(cat)} title="Edit">
                                  <Edit size={12} />
                                </button>
                                <button className="btn btn-outline" style={{ padding: '4px 8px', fontSize: 11 }}
                                  onClick={() => handleToggleCategory(cat)} title={cat.is_active ? 'Deactivate' : 'Activate'}>
                                  {cat.is_active ? <CheckCircle size={12} style={{ color: '#16a34a' }} /> : <AlertCircle size={12} style={{ color: '#d97706' }} />}
                                </button>
                                {canDelete && (
                                  <button className="btn btn-outline" style={{ padding: '4px 8px', fontSize: 11, color: '#dc2626', borderColor: '#fca5a5' }}
                                    onClick={() => handleDeleteCategory(cat)} title="Delete">
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      )
                    })}

                    {editingCatId === 'new' && (
                      <div style={{ border: '1px dashed var(--primary-navy)', borderRadius: 8, padding: 12 }}>
                        <CategoryEditForm
                          form={catForm}
                          setForm={setCatForm}
                          onSave={handleSaveCategory}
                          onCancel={() => setEditingCatId(null)}
                          saving={catSaving}
                          error={catError}
                          isNew
                          usedOrders={categories.map((c) => c.display_order)}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeCategories}>Close</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Question Type Create / Edit Modal ───────────────────────────────── */}
      {qtModalMode !== null && (
        <ModalOverlay onClose={closeQTModal}>
          <div className="modal" style={{ maxWidth: 520, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h3 className="modal-title">
                {qtModalMode === 'create' ? 'New Question Type' : 'Edit Question Type'}
              </h3>
              <button onClick={closeQTModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveQT} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Behavioral"
                    value={qtForm.name}
                    onChange={(e) => setQtForm({ ...qtForm, name: e.target.value })}
                    required
                    autoFocus
                  />
                  {qtForm.name.trim() && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                      Slug: <code style={{ background: '#f0f4ff', color: '#3730a3', padding: '1px 5px', borderRadius: 3 }}>
                        {qtForm.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}
                      </code>
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-input"
                    placeholder="Brief description of this question type…"
                    value={qtForm.description}
                    onChange={(e) => setQtForm({ ...qtForm, description: e.target.value })}
                    rows={2}
                    style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    className="form-input"
                    min={0}
                    value={qtForm.display_order}
                    onChange={(e) => setQtForm({ ...qtForm, display_order: Math.max(0, parseInt(e.target.value) || 0) })}
                    style={{ width: 100 }}
                  />
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Lower numbers appear first. Must be unique.
                    {questionTypes.length > 0 && (
                      <> Used: {questionTypes
                        .filter((t) => qtModalMode === 'edit' ? t.id !== selectedQT?.id : true)
                        .map((t) => t.display_order)
                        .sort((a, b) => a - b)
                        .join(', ')}</>
                    )}
                  </p>
                </div>

                {qtModalMode === 'create' && (
                  <div className="form-group" style={{ marginTop: 18, marginBottom: 0, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <label className="form-label">Evaluation Categories *</label>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 10px' }}>
                      Add at least one rating category interviewers will use when scoring questions of this type.
                    </p>

                    {qtDraftCategories.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                        {qtDraftCategories.map((dc, i) => (
                          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', background: '#f9fafb', border: '1px solid var(--border)', borderRadius: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', minWidth: 26 }}>#{dc.display_order}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)' }}>{dc.label}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{dc.description}</div>
                            </div>
                            <button type="button" className="btn btn-outline"
                              style={{ padding: '4px 8px', fontSize: 11, color: '#dc2626', borderColor: '#fca5a5' }}
                              onClick={() => setQtDraftCategories(qtDraftCategories.filter((_, j) => j !== i))}
                              title="Remove"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <DraftCategoryAdder
                      usedOrders={qtDraftCategories.map((c) => c.display_order)}
                      onAdd={(dc) => setQtDraftCategories([...qtDraftCategories, dc].sort((a, b) => a.display_order - b.display_order))}
                    />
                  </div>
                )}

                {qtError && (
                  <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12, padding: '10px 14px', background: 'rgba(220,38,38,0.06)', borderRadius: 8 }}>
                    {qtError}
                  </p>
                )}
              </div>

              <div className="modal-footer" style={{ flexShrink: 0, borderTop: '1px solid var(--border)', background: 'white', position: 'sticky', bottom: 0 }}>
                <button type="button" className="btn btn-secondary" onClick={closeQTModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={qtSaving}>
                  {qtSaving ? 'Saving...' : qtModalMode === 'create' ? 'Create Type' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* ── View Modal ──────────────────────────────────────────────────────── */}
      {modalMode === 'view' && selected && (
        <ModalOverlay onClose={closeModal}>
          <div className="modal" style={{ maxWidth: 680, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">{selected.title}</h3>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  {selected.target_role && <span className="badge badge-primary">{selected.target_role}</span>}
                  {selected.tier && <span className="badge badge-warning" style={{ textTransform: 'capitalize' }}>{selected.tier}</span>}
                  <span className={`badge ${selected.is_active ? 'badge-success' : 'badge-danger'}`}>{selected.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}><X size={20} /></button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {selected.description && (
                <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>{selected.description}</p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ fontSize: 15, fontWeight: 600 }}>
                  Questions <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 13 }}>({selected.questions.length})</span>
                </h4>
              </div>

              {selected.questions.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No questions added yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selected.questions.map((q, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: '#f8f9fb', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                        {idx + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>{q.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Close</button>
              {canEdit && (
                <button className="btn btn-primary" onClick={() => { closeModal(); setTimeout(() => openEdit(selected), 50) }}>
                  <Edit size={14} /> Edit Template
                </button>
              )}
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {(modalMode === 'create' || modalMode === 'edit') && (
        <ModalOverlay onClose={closeModal}>
          <div className="modal" style={{ maxWidth: 720, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3 className="modal-title">{modalMode === 'create' ? 'New Question Template' : 'Edit Template'}</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

                {/* Template metadata */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <label className="form-label">Template Title *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., Senior Software Engineer Interview"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Target Role</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., Senior HR, Product Manager"
                      value={form.target_role}
                      onChange={(e) => setForm({ ...form, target_role: e.target.value })}
                    />
                  </div>

                </div>

                {/* Questions section */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div>
                      <h4 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Interview Questions</h4>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 0' }}>
                        {formQuestions.filter((q) => q.text.trim()).length} of {formQuestions.length} questions filled
                      </p>
                    </div>
                    <button type="button" className="btn btn-outline" style={{ padding: '6px 14px', fontSize: 13 }} onClick={addQuestion}>
                      <Plus size={14} /> Add Question
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {formQuestions.map((q, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 10, padding: '14px 16px', background: '#f8f9fb', borderRadius: 10, border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 10, color: '#9ca3af', flexShrink: 0 }}>
                          <GripVertical size={16} />
                        </div>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 8 }}>
                          {idx + 1}
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <textarea
                            className="form-input"
                            placeholder={`Question ${idx + 1}...`}
                            value={q.text}
                            onChange={(e) => updateQuestion(idx, 'text', e.target.value)}
                            rows={2}
                            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
                          />
                        </div>
                        {formQuestions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeQuestion(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 6, flexShrink: 0, alignSelf: 'flex-start', marginTop: 4 }}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button type="button" className="btn btn-outline" style={{ marginTop: 12, width: '100%', justifyContent: 'center', fontSize: 13 }} onClick={addQuestion}>
                    <Plus size={14} /> Add Another Question
                  </button>
                </div>

                {error && (
                  <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12, padding: '10px 14px', background: 'rgba(220,38,38,0.06)', borderRadius: 8 }}>
                    {error}
                  </p>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modalMode === 'create' ? 'Create Template' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* ── Evaluation Review Modal ──────────────────────────────────────── */}
      {reviewEvalId !== null && (
        <div
          className="modal-overlay"
          onClick={() => { setReviewEvalId(null); fetchEvaluations(evalTab, evalPage) }}
          style={{ alignItems: 'flex-start', paddingTop: 0, overflowY: 'auto' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 860, margin: '0 auto', padding: '24px 16px 48px' }}
          >
            {/* Header bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'white', borderRadius: '12px 12px 0 0', padding: '16px 24px',
              borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Brain size={18} style={{ color: 'var(--primary-navy)' }} />
                <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Evaluation Review</h2>
              </div>
              <button
                onClick={() => { setReviewEvalId(null); fetchEvaluations(evalTab, evalPage) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: 'var(--text-muted)' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <div style={{ background: 'white', borderRadius: '0 0 12px 12px' }}>
              <EvaluationFormInline
                evalId={reviewEvalId}
                readOnly={!canEdit}
                timezone={platformTz}
                onStatusChange={() => {
                  fetchEvaluations(evalTab, evalPage)
                  if (evalTab === 'needs_evaluation') fetchEvaluations('needs_evaluation', evalPage)
                }}
              />
            </div>
          </div>
          <style>{efiStyles}</style>
        </div>
      )}

    </div>
  )
}

// ── EvaluationFormInline CSS (mirrors InterviewerEvaluations injected styles) ─

const efiStyles = `
  .efi-form {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .efi-section {
    background: #fafbfc;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 18px 20px;
  }
  .efi-session-card    { background: linear-gradient(135deg, #f0f4ff 0%, #fafbfc 100%); }
  .efi-redflag-card    { background: #fff9f9; border-color: #fecaca; }
  .efi-practice-card   { background: #f0fdff; border-color: #bae6fd; }
  .efi-obs-card        { background: #f0fdff; border-color: #a5f3fc; }
  .efi-transcript-card { background: #f5f3ff; border-color: #ddd6fe; }
  .efi-ai-card         { background: #faf5ff; border-color: #e9d5ff; }
  .efi-tracker-card    { background: white; }
  .efi-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .efi-section-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-dark);
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
  }
  .efi-section-title--strength { color: #16a34a; }
  .efi-section-title--improve  { color: #d97706; }
  .efi-status-pill {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 600;
    padding: 4px 10px; border-radius: 6px;
    flex-shrink: 0;
  }
  .efi-tag {
    font-size: 10px; font-weight: 600;
    padding: 2px 8px; border-radius: 4px;
    white-space: nowrap;
  }
  .efi-tag--red    { background: #fef2f2; color: #dc2626; }
  .efi-tag--blue   { background: #e0f2fe; color: #0369a1; }
  .efi-tag--indigo { background: #eef2ff; color: #4338ca; }
  .efi-session-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .efi-session-item {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 10px 12px;
    background: white; border-radius: 8px;
    border: 1px solid var(--border);
  }
  .efi-session-icon { color: var(--text-muted); flex-shrink: 0; margin-top: 2px; }
  .efi-session-label { font-size: 11px; color: var(--text-muted); margin-bottom: 2px; }
  .efi-session-value { font-size: 13px; font-weight: 600; color: var(--text-dark); }
  .efi-overall { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
  .efi-scores-list { display: flex; flex-direction: column; gap: 2px; }
  .efi-score-row {
    display: grid;
    grid-template-columns: 1fr auto 130px;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }
  .efi-score-row:last-child { border-bottom: none; }
  .efi-score-label { display: flex; flex-direction: column; gap: 3px; }
  .efi-score-name   { font-size: 13px; font-weight: 600; }
  .efi-score-desc   { font-size: 11px; color: var(--text-muted); margin-top: 1px; }
  .efi-score-weight {
    font-size: 10px; font-weight: 600;
    color: var(--primary-navy);
    background: rgba(30,36,50,0.07);
    display: inline-block;
    padding: 2px 7px; border-radius: 4px;
    width: fit-content; margin-top: 3px;
  }
  .efi-stars { display: flex; gap: 2px; }
  .efi-star {
    background: none; border: none; cursor: pointer; padding: 2px; color: #cbd5e1;
    transition: color 0.1s; display: flex; align-items: center;
  }
  .efi-star--filled { color: #d4af37; }
  .efi-star:disabled { cursor: default; }
  .efi-list-row {
    display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px;
  }
  .efi-list-dot {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 6px;
  }
  .efi-list-row--quote .efi-list-dot { display: none; }
  .efi-quote-input { font-style: italic; }
  .efi-line-input { flex: 1; }
  .efi-remove-btn {
    background: none; border: none; cursor: pointer; color: #9ca3af; padding: 4px;
    flex-shrink: 0;
  }
  .efi-remove-btn:hover { color: #dc2626; }
  .efi-add-btn {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; color: var(--primary-navy);
    background: none; border: 1px dashed var(--border); border-radius: 8px;
    padding: 8px 14px; cursor: pointer; width: 100%;
    justify-content: center; margin-top: 4px;
  }
  .efi-add-btn:hover { background: #f0f4ff; }
  .efi-obs-hint { font-size: 12px; color: var(--text-muted); margin-bottom: 10px; line-height: 1.5; }
  .efi-obs-empty { font-size: 12px; color: var(--text-muted); margin-top: 8px; font-style: italic; }

  /* ── Voice / recording ───────────────────────────────────────────────── */
  .efi-voice-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f0fdff; color: #0891b2;
    border: 1px solid #a5f3fc; border-radius: 8px;
    padding: 6px 14px; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: background 0.15s;
  }
  .efi-voice-btn:hover { background: #cffafe; }
  .efi-voice-btn--recording {
    background: #fef2f2; color: #dc2626;
    border-color: #fca5a5; animation: pulse-recording 1.5s ease-in-out infinite;
  }
  @keyframes pulse-recording {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  .efi-recording-indicator {
    display: flex; align-items: center; gap: 8px;
    background: #fef2f2; border: 1px solid #fca5a5;
    border-radius: 8px; padding: 8px 14px;
    font-size: 12px; font-weight: 600; color: #dc2626;
    margin-bottom: 10px;
  }
  .efi-recording-indicator--processing {
    background: #f5f3ff; border-color: #c4b5fd; color: #7c3aed;
  }
  .efi-rec-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #dc2626;
    animation: blink 1s step-start infinite;
  }
  @keyframes blink { 50% { opacity: 0; } }

  /* ── AI Phrasing Suggestions ─────────────────────────────────────────── */
  .efi-phrasing-trigger-btn {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 10px; padding: 6px 14px; font-size: 12px; font-weight: 600;
    border: 1px solid #c4b5fd; border-radius: 20px; background: #f5f3ff;
    color: #7c3aed; cursor: pointer; transition: background 0.2s;
  }
  .efi-phrasing-trigger-btn:hover { background: #ede9fe; }
  .efi-phrasing-trigger-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .efi-phrasing-panel {
    margin-top: 14px; background: #f8f7ff; border: 1px solid #ddd6fe;
    border-radius: 10px; padding: 16px; animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
  .efi-phrasing-header {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;
  }
  .efi-phrasing-title { font-size: 14px; font-weight: 700; color: #5b21b6; }
  .efi-phrasing-close {
    background: none; border: none; cursor: pointer; font-size: 16px;
    color: var(--text-muted); padding: 0 4px; line-height: 1;
  }
  .efi-phrasing-close:hover { color: #dc2626; }
  .efi-phrasing-subtitle { font-size: 12px; color: var(--text-muted); margin-bottom: 12px; }
  .efi-phrasing-list { display: flex; flex-direction: column; gap: 10px; }
  .efi-phrasing-item {
    background: white; border: 1px solid #e9d5ff; border-radius: 8px;
    padding: 12px 14px; position: relative;
  }
  .efi-phrasing-label {
    font-size: 10px; font-weight: 700; letter-spacing: 0.6px;
    color: var(--text-muted); text-transform: uppercase; margin-right: 6px;
  }
  .efi-phrasing-original { margin-bottom: 4px; }
  .efi-phrasing-original-text { font-size: 13px; color: #dc2626; text-decoration: line-through; }
  .efi-phrasing-suggested { margin-bottom: 6px; }
  .efi-phrasing-suggested-text { font-size: 13px; font-weight: 600; color: #1e293b; }
  .efi-phrasing-reason {
    display: flex; align-items: center; gap: 4px;
    font-size: 11px; color: #7c3aed; font-style: italic; margin-bottom: 10px;
  }
  .efi-phrasing-apply {
    padding: 5px 14px !important; font-size: 12px !important;
    display: inline-flex; align-items: center; gap: 4px;
  }

  /* ── VTT Transcript list ─────────────────────────────────────────────── */
  .efi-vtt { margin-bottom: 18px; }
  .efi-vtt-head {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; color: #6366f1; margin-bottom: 8px;
  }
  .efi-vtt-count {
    margin-left: auto; font-size: 11px; font-weight: 500;
    color: var(--text-muted); background: #ede9fe; padding: 2px 8px; border-radius: 10px;
  }
  .efi-vtt-list {
    border: 1px solid #ddd6fe; border-radius: 10px;
    max-height: 340px; overflow-y: auto; background: white;
  }
  .efi-vtt-row {
    display: grid; grid-template-columns: 54px 1fr; gap: 12px;
    padding: 10px 14px; border-bottom: 1px solid #ede9fe; align-items: start;
  }
  .efi-vtt-row:last-child { border-bottom: none; }
  .efi-vtt-time {
    font-size: 11px; font-weight: 600; color: #6366f1;
    white-space: nowrap; padding-top: 3px; font-variant-numeric: tabular-nums;
  }
  .efi-vtt-body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .efi-vtt-speaker {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.5px; padding: 2px 7px; border-radius: 4px; width: fit-content;
  }
  .efi-vtt-text {
    font-size: 13px; color: var(--text-dark); margin: 0; line-height: 1.55;
    overflow-wrap: break-word; word-wrap: break-word;
  }
  .efi-ai-generate-row {
    display: flex; align-items: center; gap: 10px; margin-bottom: 12px; flex-wrap: wrap;
  }
  .efi-ai-generate-btn {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600;
    padding: 8px 16px; border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed, #6d28d9); color: white;
    border: none; cursor: pointer; transition: opacity 0.15s;
  }
  .efi-ai-generate-btn:hover { opacity: 0.9; }
  .efi-ai-generate-btn:disabled { opacity: 0.5; cursor: default; }
  .efi-ai-hint { font-size: 12px; color: var(--text-muted); }
  .efi-ai-summary-box {
    display: flex; gap: 12px;
    background: white; border: 1px solid #e9d5ff;
    border-radius: 10px; padding: 14px 16px;
    margin-bottom: 12px; min-width: 0;
  }
  .efi-ai-summary-icon {
    width: 28px; height: 28px; border-radius: 8px;
    background: #f3f0ff; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .efi-ai-summary-text {
    font-size: 13px; line-height: 1.7; color: var(--text-dark); margin: 0;
    flex: 1; min-width: 0; overflow-wrap: break-word; word-break: break-word;
    white-space: pre-wrap;
  }
  .efi-ai-summary-editable-wrap { margin-bottom: 12px; }
  .efi-ai-summary-badge {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 600; color: #7c3aed;
    background: #f5f3ff; border: 1px solid #e9d5ff;
    border-radius: 6px; padding: 4px 10px; margin-bottom: 8px;
  }
  .efi-ai-summary-textarea {
    width: 100%; box-sizing: border-box;
    font-size: 13px; line-height: 1.7; color: var(--text-dark);
    border: 1.5px solid #e9d5ff; border-radius: 10px;
    padding: 14px 16px; resize: vertical; min-height: 160px;
    font-family: inherit; background: white;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .efi-ai-summary-textarea:focus {
    outline: none; border-color: #a78bfa;
    box-shadow: 0 0 0 3px rgba(167,139,250,0.15);
  }
  .efi-ai-empty {
    display: flex; align-items: center; gap: 12px;
    background: white; border: 1px dashed #ddd6fe;
    border-radius: 10px; padding: 14px 16px; margin-bottom: 12px;
    font-size: 13px; color: var(--text-muted); line-height: 1.5;
  }
  .efi-ai-approve-row {
    display: flex; align-items: flex-start; gap: 10px;
    margin-top: 14px; padding: 12px 14px;
    background: white; border: 1px solid #e9d5ff;
    border-radius: 8px; cursor: pointer;
  }
  .efi-ai-approve-text { font-size: 12px; font-weight: 500; color: #6b21a8; line-height: 1.5; flex: 1; }
  .efi-ai-approved-badge {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 11px; font-weight: 700; color: #16a34a;
    background: #f0fdf4; border: 1px solid #bbf7d0;
    padding: 3px 9px; border-radius: 5px; white-space: nowrap; flex-shrink: 0;
  }
  .efi-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: var(--primary-navy); flex-shrink: 0; }
  .efi-approval-card {
    background: #fffbeb; border: 1px solid #fde68a;
    border-radius: 12px; padding: 18px 20px;
  }
  .efi-approval-header { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 14px; }
  .efi-approval-title { font-size: 14px; font-weight: 700; color: #92400e; margin: 0 0 4px; }
  .efi-approval-desc  { font-size: 12px; color: #a16207; margin: 0; }
  .efi-approval-check-wrap {
    display: block; padding: 12px 14px;
    background: white; border: 1px solid #fde68a; border-radius: 8px;
  }
  .efi-approval-check-inner { display: flex; align-items: flex-start; gap: 10px; }
  .efi-approval-check-text { font-size: 13px; color: #92400e; flex: 1; }
  .efi-approved-inline {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 11px; font-weight: 600; color: #16a34a;
    background: #f0fdf4; padding: 2px 8px; border-radius: 20px; flex-shrink: 0;
  }
  .efi-delivered-notice {
    display: flex; align-items: center; gap: 8px;
    padding: 12px 16px; background: #f0fdf4; border: 1px solid #bbf7d0;
    border-radius: 10px; font-size: 13px; color: #15803d; font-weight: 500;
  }
  .efi-tracker {
    display: flex; align-items: flex-start; gap: 0; position: relative; margin-bottom: 28px;
  }
  .efi-tracker-item {
    flex: 1; display: flex; flex-direction: column; align-items: center; position: relative;
  }
  .efi-tracker-line {
    height: 3px; width: 100%; background: #e5e7eb; border-radius: 2px;
    margin-top: 12px;
    transition: background 0.3s;
  }
  .efi-tracker-line--done   { background: #16a34a; }
  .efi-tracker-line--active { background: linear-gradient(90deg, #16a34a, #e5e7eb); }
  .efi-tracker-dot {
    width: 24px; height: 24px; border-radius: 50%;
    background: #e5e7eb; display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; color: #9ca3af;
    transition: all 0.3s; flex-shrink: 0; border: 2px solid white;
    box-shadow: 0 0 0 2px #e5e7eb;
    position: absolute; top: 1px; left: 50%; transform: translateX(-50%);
  }
  .efi-tracker-dot--done   { background: #16a34a; color: white; box-shadow: 0 0 0 2px #bbf7d0; }
  .efi-tracker-dot--active { background: var(--primary-navy); color: white; box-shadow: 0 0 0 2px #c7d2fe; }
  .efi-tracker-label {
    font-size: 10px; font-weight: 600; text-align: center;
    margin-top: 30px; color: var(--text-muted);
    white-space: nowrap;
  }
  .efi-tracker-label--active { color: var(--primary-navy); }
  .efi-tracker-label--done   { color: #16a34a; }
  .efi-tracker-desc {
    font-size: 9px; color: var(--text-muted); text-align: center; margin-top: 2px;
    white-space: nowrap;
  }
  .efi-tracker-actions {
    display: flex; align-items: center; justify-content: flex-end;
    gap: 12px; flex-wrap: wrap; margin-top: 20px;
  }
  .efi-save-msg { font-size: 13px; color: #16a34a; font-weight: 600; }
  .efi-approve-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-success {
    background: #16a34a; color: white; border-color: #16a34a;
  }
  .btn-success:hover:not(:disabled) { background: #15803d; }
`

// ── Sub-components ────────────────────────────────────────────────────────────

function EvalPagination({
  page,
  totalPages,
  total,
  onPageChange,
}: {
  page: number
  totalPages: number
  total: number
  onPageChange: (p: number) => void
}) {
  const start = (page - 1) * EVAL_PER_PAGE + 1
  const end = Math.min(page * EVAL_PER_PAGE, total)
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, padding: '0 4px' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        Showing {start}–{end} of {total} session{total !== 1 ? 's' : ''}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-secondary"
          style={{ padding: '6px 14px', fontSize: 13 }}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
          {page} / {totalPages}
        </span>
        <button
          className="btn btn-secondary"
          style={{ padding: '6px 14px', fontSize: 13 }}
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

function EvalStatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    draft:               { label: 'Draft',          cls: 'badge-warning', icon: <FileText size={10} /> },
    ai_pending:          { label: 'AI Pending',     cls: 'badge-primary', icon: <Brain size={10} /> },
    interviewer_review:  { label: 'Under Review',   cls: 'badge-primary', icon: <Clock size={10} /> },
    approved:            { label: 'Approved',       cls: 'badge-success', icon: <CheckCircle size={10} /> },
    delivered:           { label: 'Sent',           cls: 'badge-success', icon: <Send size={10} /> },
  }
  const c = cfg[status] ?? { label: status, cls: 'badge-warning', icon: null }
  return (
    <span className={`badge ${c.cls}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {c.icon}{c.label}
    </span>
  )
}

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ alignItems: 'flex-start', paddingTop: 40 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  )
}

function TemplateCard({
  template: t,
  canEdit, canDelete,
  onView, onEdit, onDelete,
}: {
  template: QuestionTemplate
  canEdit: boolean
  canDelete: boolean
  onView: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  const tierColor: Record<string, string> = {
    standard: '#2563eb', technical: '#7c3aed', executive: '#d97706',
  }
  const color = tierColor[t.tier ?? ''] ?? '#6b7280'

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 12,
      overflow: 'hidden',
      background: 'white',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Card header stripe */}
      <div style={{ height: 4, background: color }} />

      <div style={{ padding: '16px 18px', flex: 1 }}>
        {/* Top row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <span className={`badge ${t.is_active ? 'badge-success' : 'badge-warning'}`}>{t.is_active ? 'Active' : 'Inactive'}</span>
          {t.tier && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: `${color}18`, color, textTransform: 'capitalize' }}>
              {t.tier}
            </span>
          )}
        </div>

        <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, lineHeight: 1.3 }}>{t.title}</h4>

        {t.target_role && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <FileText size={12} /> {t.target_role}
          </p>
        )}

        {t.description && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as React.CSSProperties}>
            {t.description}
          </p>
        )}

        {/* Question count */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
          {t.questions.length} question{t.questions.length !== 1 ? 's' : ''}
        </div>

        {/* Preview toggle */}
        {t.questions.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary-navy)', display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontWeight: 500, marginBottom: expanded ? 10 : 0 }}
            >
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {expanded ? 'Hide preview' : 'Preview questions'}
            </button>

            {expanded && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                {t.questions.slice(0, 5).map((q, i) => (
                  <div key={i} style={{ fontSize: 12, padding: '7px 10px', background: '#f8f9fb', borderRadius: 7, lineHeight: 1.4, display: 'flex', gap: 8 }}>
                    <span style={{ fontWeight: 700, color: 'var(--primary-navy)', flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ color: 'var(--text-dark)' }}>{q.text}</span>
                  </div>
                ))}
                {t.questions.length > 5 && (
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, textAlign: 'center' }}>+{t.questions.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--border)' }}>
        <button onClick={onView} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', borderRight: canEdit || canDelete ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', fontWeight: 500, transition: 'all 0.15s' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
          <Eye size={14} /> View
        </button>
        {canEdit && (
          <button onClick={onEdit} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', borderRight: canDelete ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', fontWeight: 500, transition: 'all 0.15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
            <Edit size={14} /> Edit
          </button>
        )}
        {canDelete && (
          <button onClick={onDelete} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--danger)', fontWeight: 500, transition: 'all 0.15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(220,38,38,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
            <Trash2 size={14} /> Delete
          </button>
        )}
      </div>
    </div>
  )
}

// ── CategoryEditForm ──────────────────────────────────────────────────────────

interface CategoryEditFormProps {
  form: { key: string; label: string; description: string; display_order: number }
  setForm: (f: { key: string; label: string; description: string; display_order: number }) => void
  onSave: (e: React.FormEvent) => void
  onCancel: () => void
  saving: boolean
  error: string
  isNew?: boolean
  usedOrders?: number[]  // display orders already in use by OTHER categories
}

function CategoryEditForm({ form, setForm, onSave, onCancel, saving, error, isNew, usedOrders = [] }: CategoryEditFormProps) {
  const orderClash = usedOrders.includes(form.display_order)
  return (
    <form onSubmit={onSave} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Label *</label>
          <input
            className="form-input"
            placeholder="e.g., Contribution"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            autoFocus={isNew}
            required
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Order *</label>
          <input
            type="number"
            className="form-input"
            min={1}
            value={form.display_order}
            onChange={(e) => setForm({ ...form, display_order: Math.max(1, parseInt(e.target.value) || 1) })}
            style={{ borderColor: orderClash ? '#dc2626' : undefined }}
          />
        </div>
      </div>
      {usedOrders.length > 0 && (
        <p style={{ fontSize: 11, color: orderClash ? '#dc2626' : 'var(--text-muted)', margin: 0 }}>
          {orderClash
            ? `Order ${form.display_order} is already used — pick a different number.`
            : `In use: ${[...usedOrders].sort((a, b) => a - b).join(', ')}`}
        </p>
      )}
      <div>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Helper text *</label>
        <textarea
          className="form-input"
          placeholder="One-sentence hint shown to the interviewer when rating this category."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
          style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
          required
        />
      </div>
      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 12, margin: 0, padding: '6px 10px', background: 'rgba(220,38,38,0.06)', borderRadius: 6 }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" style={{ padding: '6px 12px', fontSize: 13 }} disabled={saving || orderClash || !form.label.trim() || !form.description.trim()}>
          {saving ? 'Saving…' : isNew ? 'Add Category' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// ── DraftCategoryAdder ────────────────────────────────────────────────────────
// Compact inline form used inside the "New Question Type" modal to collect
// initial categories. Enforces unique display_order among drafts.

interface DraftCategoryAdderProps {
  usedOrders: number[]
  onAdd: (dc: { label: string; description: string; display_order: number }) => void
}

function DraftCategoryAdder({ usedOrders, onAdd }: DraftCategoryAdderProps) {
  const nextOrder = (() => {
    const used = new Set(usedOrders)
    let n = 1
    while (used.has(n)) n++
    return n
  })()
  const [label, setLabel] = useState('')
  const [desc, setDesc] = useState('')
  const [order, setOrder] = useState(nextOrder)
  const [localErr, setLocalErr] = useState('')

  // Re-sync suggested order when the list of used orders changes
  useEffect(() => { setOrder(nextOrder) }, [usedOrders.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const orderClash = usedOrders.includes(order)

  function handleAdd() {
    setLocalErr('')
    if (!label.trim()) { setLocalErr('Label is required.'); return }
    if (!desc.trim()) { setLocalErr('Helper text is required.'); return }
    if (orderClash) { setLocalErr(`Order ${order} is already used — pick a different number.`); return }
    onAdd({ label: label.trim(), description: desc.trim(), display_order: order })
    setLabel('')
    setDesc('')
  }

  return (
    <div style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 8 }}>
        <input
          className="form-input"
          placeholder="Category label (e.g., Contribution)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{ fontSize: 13 }}
        />
        <input
          type="number"
          className="form-input"
          min={1}
          value={order}
          onChange={(e) => setOrder(Math.max(1, parseInt(e.target.value) || 1))}
          style={{ fontSize: 13, borderColor: orderClash ? '#dc2626' : undefined }}
          title="Display order (unique)"
        />
      </div>
      <textarea
        className="form-input"
        placeholder="Helper text shown to the interviewer (one sentence)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        rows={2}
        style={{ fontSize: 12, fontFamily: 'inherit', resize: 'vertical' }}
      />
      {(usedOrders.length > 0 || orderClash) && (
        <p style={{ fontSize: 11, color: orderClash ? '#dc2626' : 'var(--text-muted)', margin: 0 }}>
          {orderClash
            ? `Order ${order} is already used — pick a different number.`
            : `In use: ${[...usedOrders].sort((a, b) => a - b).join(', ')}`}
        </p>
      )}
      {localErr && (
        <p style={{ color: 'var(--danger)', fontSize: 12, margin: 0 }}>{localErr}</p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" style={{ padding: '5px 12px', fontSize: 12 }} onClick={handleAdd}>
          <Plus size={12} /> Add category
        </button>
      </div>
    </div>
  )
}
